import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Brain, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, X, RotateCcw } from 'lucide-react';
import { toast } from 'react-hot-toast';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LearningProgressProps {
  isOpen: boolean;
  onClose: () => void;
}

const LearningProgress: React.FC<LearningProgressProps> = ({ isOpen, onClose }) => {
  const [viewWindow, setViewWindow] = useState({ start: 0, size: 8 });
  const [zoomLevel, setZoomLevel] = useState(1);

  const resetLearningData = () => {
    if (window.confirm('Are you sure you want to reset all learning progress? This cannot be undone.')) {
      localStorage.setItem('rlReplayBuffer', '[]');
      localStorage.setItem('rlModel', '{}');
      toast.success('Learning progress has been reset');
    }
  };

  const getRLData = () => {
    const buffer = JSON.parse(localStorage.getItem('rlReplayBuffer') || '[]');
    const model = JSON.parse(localStorage.getItem('rlModel') || '{}');

    const allTimeLabels = buffer.map((exp: any) => {
      const date = new Date(exp.timestamp);
      return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
    });
    const allRewards = buffer.map((exp: any) => exp.reward);

    const start = Math.max(0, allRewards.length - viewWindow.size - viewWindow.start);
    const end = Math.max(viewWindow.size, allRewards.length - viewWindow.start);

    const timeLabels = allTimeLabels.slice(start, end);
    const rewards = allRewards.slice(start, end);
    const actionScores = Object.entries(model).map(([action, score]) => ({
      action,
      score: Number(score)
    }));

    return {
      timeLabels,
      rewards,
      actionScores,
      totalPoints: buffer.length
    };
  };

  const { timeLabels, rewards, actionScores, totalPoints } = useMemo(getRLData, [viewWindow, zoomLevel]);

  useEffect(() => {
    setViewWindow(prev => ({
      ...prev,
      size: Math.floor(16 / zoomLevel)
    }));
  }, [zoomLevel]);

  const handleScroll = (direction: 'left' | 'right') => {
    setViewWindow(prev => ({
      ...prev,
      start: Math.max(0, direction === 'left' ?
        prev.start - Math.floor(prev.size / 2) :
        prev.start + Math.floor(prev.size / 2))
    }));
  };

  const handleZoom = (direction: 'in' | 'out') => {
    setZoomLevel(prev => {
      const newZoom = direction === 'in' ? prev + 0.5 : prev - 0.5;
      return Math.max(0.5, Math.min(2, newZoom));
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-start justify-center px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain touch-pan-y">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <Brain className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">AI Learning Progress</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetLearningData}
              className="p-2 hover:bg-red-100 rounded-full transition-colors text-red-600"
              title="Reset learning progress"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {/* Learning History Chart */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleZoom('out')}
                  disabled={zoomLevel <= 0.5}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  title="Zoom out"
                >
                  <ZoomOut className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={() => handleZoom('in')}
                  disabled={zoomLevel >= 2}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  title="Zoom in"
                >
                  <ZoomIn className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={() => handleScroll('left')}
                  disabled={viewWindow.start === 0}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  title="Scroll left"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={() => handleScroll('right')}
                  disabled={viewWindow.start + viewWindow.size >= totalPoints}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  title="Scroll right"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 h-[200px]">
              <Line
                data={{
                  labels: timeLabels,
                  datasets: [
                    {
                      label: 'Learning Score',
                      data: rewards,
                      borderColor: 'rgb(37, 99, 235)',
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      fill: true,
                      tension: 0.4
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      titleColor: '#1e40af',
                      bodyColor: '#1e40af',
                      padding: 12,
                      cornerRadius: 8,
                      boxPadding: 6,
                      callbacks: {
                        title: (items) => `Time: ${items[0].label}`,
                        label: (item) => `Score: ${item.raw.toFixed(2)}`
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  interaction: {
                    intersect: false,
                    mode: 'index'
                  }
                }}
              />
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>Showing {viewWindow.size} of {totalPoints} experiences</span>
              <span>Zoom: {(zoomLevel * 100).toFixed(0)}%</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex flex-col">
                <h4 className="text-sm font-medium text-blue-900">Total Experiences</h4>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-2xl font-bold text-blue-600">{totalPoints}</p>
                  <span className="text-sm text-blue-500">Total interactions</span>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex flex-col">
                <h4 className="text-sm font-medium text-green-900">Average Score</h4>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-2xl font-bold text-green-600">
                    {(rewards.reduce((a, b) => a + b, 0) / rewards.length || 0).toFixed(2)}
                  </p>
                  <span className="text-sm text-green-500">Learning rate</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Performance */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Action Performance</h3>
            <div className="grid gap-3">
              {actionScores.map(({ action, score }) => (
                <div key={action} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {action.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">{score.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 rounded-full h-2 transition-all duration-500"
                      style={{ width: `${Math.min(Math.max((score / 5) * 100, 0), 100)}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {score > 0 ? 'Positive learning trend' : score < 0 ? 'Needs improvement' : 'Neutral performance'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Learning Tips */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h3 className="text-lg font-medium text-blue-900 mb-3">Learning Tips</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-blue-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Give feedback to help the AI learn from interactions
              </li>
              <li className="flex items-center gap-3 text-blue-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Use thumbs up/down to rate responses
              </li>
              <li className="flex items-center gap-3 text-blue-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Consistent feedback improves AI performance
              </li>
            </ul>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 text-center">
            Learning progress is updated in real-time as you interact with the AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default LearningProgress;
